import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, Shield, Trash2, Plus, Loader2, UserPlus, Eye, 
  Settings, Database, Archive, AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  all: "Alle Status",
  new: "Neu",
  contacted: "Kann erst später",
  qualified: "Qualifiziert",
  unsuitable: "Ungeeignet",
  contract_sent: "Weitergeleitet an T2M",
  hired: "Eingestellt",
  waitlist: "Nicht erreicht",
  duplicate: "Doppelt"
};

export const AdminSettings = ({ authToken, userRole }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null, username: "" });
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "viewer" });
  const [isCreating, setIsCreating] = useState(false);
  
  // Bulk actions state
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, count: 0, status: "all" });
  const [bulkArchiveDialog, setBulkArchiveDialog] = useState({ open: false, count: 0, archive: true, status: "all" });
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Test data state
  const [testDataDialog, setTestDataDialog] = useState({ open: false, count: 50 });
  const [isGenerating, setIsGenerating] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    if (userRole === "admin") {
      fetchUsers();
    } else {
      setIsLoading(false);
    }
  }, [userRole]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    
    setIsCreating(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, getAuthHeaders());
      toast.success("Benutzer erstellt");
      setIsAddUserOpen(false);
      setNewUser({ username: "", password: "", role: "viewer" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${deleteUserDialog.userId}`, getAuthHeaders());
      toast.success("Benutzer gelöscht");
      setDeleteUserDialog({ open: false, userId: null, username: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleBulkDelete = async () => {
    setIsProcessing(true);
    try {
      const params = { count: bulkDeleteDialog.count };
      if (bulkDeleteDialog.status !== "all") {
        params.status = bulkDeleteDialog.status;
      }
      
      const response = await axios.post(`${API}/admin/leads/delete-by-count`, null, {
        params,
        ...getAuthHeaders()
      });
      
      toast.success(`${response.data.deleted} Leads gelöscht`);
      setBulkDeleteDialog({ open: false, count: 0, status: "all" });
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkArchive = async () => {
    setIsProcessing(true);
    try {
      const params = { 
        count: bulkArchiveDialog.count,
        archive: bulkArchiveDialog.archive
      };
      if (bulkArchiveDialog.status !== "all") {
        params.status = bulkArchiveDialog.status;
      }
      
      const response = await axios.post(`${API}/admin/leads/archive-by-count`, null, {
        params,
        ...getAuthHeaders()
      });
      
      toast.success(`${response.data.updated} Leads ${bulkArchiveDialog.archive ? 'archiviert' : 'wiederhergestellt'}`);
      setBulkArchiveDialog({ open: false, count: 0, archive: true, status: "all" });
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateTestData = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/generate-test-data`, null, {
        params: { count: testDataDialog.count },
        ...getAuthHeaders()
      });
      
      toast.success(`${response.data.created} Test-Leads erstellt`);
      setTestDataDialog({ open: false, count: 50 });
    } catch (error) {
      toast.error("Fehler beim Generieren");
    } finally {
      setIsGenerating(false);
    }
  };

  if (userRole !== "admin") {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-16 text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nur Admins haben Zugriff auf die Einstellungen.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* User Management */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-400" />
              Benutzerverwaltung
            </CardTitle>
            <CardDescription>Admin- und Viewer-Benutzer verwalten</CardDescription>
          </div>
          <Button onClick={() => setIsAddUserOpen(true)} className="rounded-lg">
            <Plus className="w-4 h-4 mr-2" />
            Benutzer hinzufügen
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div 
                  key={user.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-semibold">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.username}</p>
                      <p className="text-xs text-slate-500">Erstellt: {new Date(user.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={user.role === "admin" ? "default" : "secondary"} className="capitalize">
                      {user.role === "admin" ? (
                        <><Shield className="w-3 h-3 mr-1" /> Admin</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1" /> Viewer</>
                      )}
                    </Badge>
                    {user.username !== "admin" && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteUserDialog({ open: true, userId: user.id, username: user.username })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Data Management */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5 text-slate-400" />
            Datenverwaltung
          </CardTitle>
          <CardDescription>Massenlöschung und Archivierung von Leads</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Bulk Delete */}
          <div className="p-4 bg-red-50 rounded-lg border border-red-100">
            <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
              <Trash2 className="w-4 h-4" />
              Leads löschen
            </h4>
            <div className="flex flex-wrap gap-2">
              {[10, 20, 50, 100].map((count) => (
                <Button 
                  key={count}
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-100"
                  onClick={() => setBulkDeleteDialog({ open: true, count, status: "all" })}
                >
                  {count} löschen
                </Button>
              ))}
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-100"
                onClick={() => setBulkDeleteDialog({ open: true, count: -1, status: "all" })}
              >
                Alle löschen
              </Button>
            </div>
          </div>

          {/* Bulk Archive */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
              <Archive className="w-4 h-4" />
              Leads archivieren
            </h4>
            <div className="flex flex-wrap gap-2">
              {[10, 20, 50, 100].map((count) => (
                <Button 
                  key={count}
                  variant="outline" 
                  size="sm"
                  className="text-amber-600 border-amber-200 hover:bg-amber-100"
                  onClick={() => setBulkArchiveDialog({ open: true, count, archive: true, status: "all" })}
                >
                  {count} archivieren
                </Button>
              ))}
              <Button 
                variant="outline" 
                size="sm"
                className="text-amber-600 border-amber-200 hover:bg-amber-100"
                onClick={() => setBulkArchiveDialog({ open: true, count: -1, archive: true, status: "all" })}
              >
                Alle archivieren
              </Button>
            </div>
          </div>

          {/* Test Data Generation */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
              <Database className="w-4 h-4" />
              Testdaten generieren
            </h4>
            <p className="text-sm text-blue-600 mb-3">Generiere Test-Leads mit Chat-Verläufen für Demo-Zwecke.</p>
            <div className="flex flex-wrap gap-2">
              {[10, 25, 50, 100].map((count) => (
                <Button 
                  key={count}
                  variant="outline" 
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-100"
                  onClick={() => setTestDataDialog({ open: true, count })}
                >
                  {count} Test-Leads
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Neuen Benutzer erstellen
            </DialogTitle>
            <DialogDescription>
              Erstelle einen Admin- oder Viewer-Benutzer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Benutzername</Label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="z.B. maria"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>Passwort</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Sicheres Passwort"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Viewer (nur lesen)
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin (voller Zugriff)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleCreateUser} 
              disabled={isCreating}
              className="w-full rounded-lg"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Benutzer erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ ...deleteUserDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Benutzer "{deleteUserDialog.username}" wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 rounded-lg">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialog.open} onOpenChange={(open) => setBulkDeleteDialog({ ...bulkDeleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Leads löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkDeleteDialog.count === -1 
                ? "ALLE Leads werden dauerhaft gelöscht!" 
                : `Die ältesten ${bulkDeleteDialog.count} Leads werden dauerhaft gelöscht.`}
              <br />Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" disabled={isProcessing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-red-600 hover:bg-red-700 rounded-lg"
              disabled={isProcessing}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Archive Dialog */}
      <AlertDialog open={bulkArchiveDialog.open} onOpenChange={(open) => setBulkArchiveDialog({ ...bulkArchiveDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leads archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkArchiveDialog.count === -1 
                ? "ALLE Leads werden archiviert." 
                : `Die ältesten ${bulkArchiveDialog.count} Leads werden archiviert.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" disabled={isProcessing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkArchive} 
              className="bg-amber-600 hover:bg-amber-700 rounded-lg"
              disabled={isProcessing}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Data Dialog */}
      <AlertDialog open={testDataDialog.open} onOpenChange={(open) => setTestDataDialog({ ...testDataDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Testdaten generieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {testDataDialog.count} Test-Leads mit Chat-Verläufen werden erstellt.
              Diese Daten sind nach dem Deployment im Live-Admin sichtbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" disabled={isGenerating}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleGenerateTestData} 
              className="bg-blue-600 hover:bg-blue-700 rounded-lg"
              disabled={isGenerating}
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSettings;
